import React from 'react';
import {
  useRequestResponseDetail,
  useRequestResponseSubmit,
} from '@/core/domain/crm/RequestResponse/RequestResponse.hooks';
import { useDefaultEmptyFieldError, useRouteMatchParam } from '@/hooks';
import LayoutPublicHeader from '@/layouts/PublicLayout/components/PublicHeader';
import {
  Button,
  Card,
  Checkbox,
  Form,
  FormItemProps,
  Input,
  Layout,
  Radio,
  Rate,
  RateProps,
  Result,
  Skeleton,
  Space,
  Typography,
  message,
} from 'antd';
import { RequestResponseQuestion } from '@/core/domain/crm/RequestResponse/RequestResponse.types';
import { QuestionTypeEnum } from '@/core/domain/crm/Questions/Questions.types';
import './style.less';
import { Group } from '@/components/_common';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormValues = Record<string, any>;

const RateCustom = React.forwardRef<
  unknown,
  RateProps & { question: RequestResponseQuestion }
>(({ question, ...props }, ref) => {
  const form = Form.useFormInstance();

  return (
    <Group gap={10}>
      <span
        className="cursor-pointer"
        onClick={() => form.setFieldValue(question.id, 1)}
      >
        {question.lowerEnd}
      </span>

      <Rate {...props} ref={ref} />

      <span
        className="cursor-pointer"
        onClick={() => form.setFieldValue(question.id, 5)}
      >
        {question.topEnd}
      </span>
    </Group>
  );
});

const CrmQuizPage: React.FC = () => {
  const requestCode = useRouteMatchParam('requestCode');
  const [form] = Form.useForm<FormValues>();
  const ruleRequired = useDefaultEmptyFieldError();

  const { data, isLoading } = useRequestResponseDetail(requestCode);

  const requestResponseSubmit = useRequestResponseSubmit();

  const getNameField = (question: RequestResponseQuestion) =>
    `${question.type}-${question.id}`;

  const formItemProps = (
    question: RequestResponseQuestion,
    props?: FormItemProps,
  ): FormItemProps => ({
    label: `${question.questionSequence}) ${question.description}`,
    name: getNameField(question),
    rules: [ruleRequired],
    className: 'crm-quiz-form-item',
    ...props,
  });

  function renderComment(question: RequestResponseQuestion) {
    return (
      <Form.Item shouldUpdate>
        {({ getFieldError }) => (
          <Card
            style={{
              borderColor: getFieldError(getNameField(question)).length
                ? 'red'
                : undefined,
            }}
          >
            <Form.Item {...formItemProps(question)}>
              <Input.TextArea rows={4} />
            </Form.Item>
          </Card>
        )}
      </Form.Item>
    );
  }

  function renderMultiple(question: RequestResponseQuestion) {
    return (
      <Form.Item shouldUpdate>
        {({ getFieldError }) => (
          <Card
            style={{
              borderColor: getFieldError(getNameField(question)).length
                ? 'red'
                : undefined,
            }}
          >
            <Form.Item {...formItemProps(question)}>
              <Checkbox.Group>
                {question.optionsAnswer
                  .sort(
                    (a, b) => a.answerOptionSequence - b.answerOptionSequence,
                  )
                  .map((option) => (
                    <Checkbox value={option.id}>{option.description}</Checkbox>
                  ))}
              </Checkbox.Group>
            </Form.Item>
          </Card>
        )}
      </Form.Item>
    );
  }

  function renderSingle(question: RequestResponseQuestion) {
    return (
      <Form.Item shouldUpdate>
        {({ getFieldError }) => (
          <Card
            style={{
              borderColor: getFieldError(getNameField(question)).length
                ? 'red'
                : undefined,
            }}
          >
            <Form.Item {...formItemProps(question)}>
              <Radio.Group>
                {question.optionsAnswer
                  .sort(
                    (a, b) => a.answerOptionSequence - b.answerOptionSequence,
                  )
                  .map((option) => (
                    <Radio value={option.id}>{option.description}</Radio>
                  ))}
              </Radio.Group>
            </Form.Item>
          </Card>
        )}
      </Form.Item>
    );
  }

  function renderClassification(question: RequestResponseQuestion) {
    return (
      <Form.Item shouldUpdate>
        {({ getFieldError }) => (
          <Card
            style={{
              borderColor: getFieldError(getNameField(question)).length
                ? 'red'
                : undefined,
            }}
          >
            <Form.Item
              {...formItemProps(question, {
                rules: [
                  {
                    type: 'number',
                    min: 1,
                    max: 5,
                    ...ruleRequired,
                  },
                ],
              })}
            >
              <RateCustom question={question} />
            </Form.Item>
          </Card>
        )}
      </Form.Item>
    );
  }

  async function handleSubmit(values: FormValues) {
    try {
      if (!data?.id) {
        return message.warning('O questionário não é válido');
      }

      await requestResponseSubmit.mutateAsync({
        id: data?.id,
        answers: Object.keys(values).map((key) => {
          const [type, questionId] = key.split('-');

          return {
            questionId: Number(questionId),
            comment:
              type === QuestionTypeEnum.Comments ? values[key] : undefined,
            stars:
              type === QuestionTypeEnum.Classification
                ? values[key]
                : undefined,
            selectedOptions:
              type === QuestionTypeEnum.MultipleAnswers ||
              type === QuestionTypeEnum.SingleAnswer
                ? Array.isArray(values[key])
                  ? values[key]
                  : [values[key]]
                : undefined,
          };
        }),
      });
    } catch {}
  }

  if (requestResponseSubmit.data) {
    return (
      <Layout style={{ minHeight: '100vh' }} className="layout-fill">
        <LayoutPublicHeader />
        <div>
          <Result
            status="success"
            title="Questionário respondido!"
            subTitle={`Agradecemos por responder o questionário "${data?.quiz?.description}"!`}
          />
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout style={{ minHeight: '100vh' }} className="layout-fill">
        <LayoutPublicHeader />
        <div>
          <main className="crm-quiz-root">
            <Skeleton loading={isLoading}>
              {!!data && !!data?.answered && (
                <Result
                  status="success"
                  title="Questionário respondido!"
                  subTitle="Este questionário já foi respondido pelo cliente."
                />
              )}

              {!!data && !data?.answered && (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                  <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size="large"
                  >
                    <div>
                      <Typography.Title level={2} style={{ margin: 0 }}>
                        {data?.quiz?.description}
                      </Typography.Title>

                      <Typography.Text>
                        <span
                          style={{
                            fontSize: 14,
                            color: 'red',
                            lineHeight: 1,
                            display: 'inline-block',
                            fontFamily: 'SimSun, sans-serif',
                          }}
                        >
                          *
                        </span>{' '}
                        Campos obrigatórios
                      </Typography.Text>
                    </div>

                    {!!data?.quiz?.openingMessage && (
                      <Card>{data?.quiz?.openingMessage}</Card>
                    )}

                    {data?.quiz?.questions
                      .sort((a, b) => a.questionSequence - b.questionSequence)
                      .map((question) => (
                        <div key={question.id}>
                          {question.type === QuestionTypeEnum.Comments &&
                            renderComment(question)}

                          {question.type === QuestionTypeEnum.MultipleAnswers &&
                            renderMultiple(question)}

                          {question.type === QuestionTypeEnum.SingleAnswer &&
                            renderSingle(question)}

                          {question.type === QuestionTypeEnum.Classification &&
                            renderClassification(question)}
                        </div>
                      ))}

                    {!!data?.quiz?.finalMessage && (
                      <Card>{data?.quiz?.finalMessage}</Card>
                    )}

                    <Group position="center">
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={requestResponseSubmit.isLoading}
                      >
                        Enviar
                      </Button>
                    </Group>
                  </Space>
                </Form>
              )}
            </Skeleton>
          </main>
        </div>
      </Layout>
    </>
  );
};

export default CrmQuizPage;
